import { CONFIG } from '../config';
import { BLACKOUTS } from '../content/blackouts';
import { dayName, minutesUntilWake } from '../state/rules';
import type { Driving } from './Driving';
import type { GameServices } from './services';

export type PassOutReason = 'late' | 'exhausted' | 'blackout';

/** Sleeping, napping, blacking out and the emergency room: everything that skips the clock. */
export class RestCycle {
  sleeping = false;
  /** Puts you back in bed if you slept indoors; returns false to wake at the front door. */
  wakeInside: () => boolean = () => false;

  constructor(
    private readonly s: GameServices,
    private readonly driving: Driving,
  ) {}

  sleep(): void {
    const { store, ui } = this.s;
    this.sleeping = true;
    const hungover = store.stat('buzz') > 50;
    const nextDay = store.day + (store.minutes >= CONFIG.wakeMinutes ? 1 : 0);
    ui.hud.showFade('Z Z Z', `${dayName(nextDay)} is coming…`);
    this.s.sfx('snore');
    window.setTimeout(() => {
      this.skipTo(minutesUntilWake(store.minutes));
      const inBed = this.wakeInside();
      if (hungover) {
        store.addStat('health', -10);
        store.addStat('energy', -20);
      }
      if (!inBed) this.wakeAtDoor();
      this.s.save();
      window.setTimeout(() => {
        this.finishFade();
        const r = store.rentStatus();
        store.toast(
          `Rise and shine! ${dayName(store.day)}, Day ${store.day}.${r.paid ? '' : ` Rent $${r.amount} due ${r.daysUntil <= 0 ? 'TODAY at 5 PM' : `in ${r.daysUntil} day(s)`}.`}`,
          r.daysUntil <= 0 && !r.paid ? 'bad' : 'good',
          ':sun:',
        );
        if (hungover) store.toast('Hangover. Your head is a marching band. -10 :heart:', 'bad', ':hurt:');
      }, 700);
    }, 1600);
  }

  nap(): void {
    this.sleeping = true;
    this.s.ui.hud.showFade('z z z', 'Just resting your eyes…');
    window.setTimeout(() => {
      this.skipTo(CONFIG.napMinutes);
      if (!this.wakeInside()) this.wakeAtDoor();
      window.setTimeout(() => this.finishFade(), 600);
    }, 1200);
  }

  passOut(reason: PassOutReason): void {
    if (this.sleeping) return;
    const { store, rng, ui, player } = this.s;
    this.interruptEverything();
    if (player.carrying || player.pushing) this.s.jobs.cancel(false);
    this.sleeping = true;
    ui.hud.showFade(
      reason === 'blackout' ? ':beer: BLACKED OUT' : ':knocked-out: PASSED OUT',
      reason === 'late'
        ? 'It was 3 AM. Your body made an executive decision.'
        : 'Your body has left the chat.',
    );
    this.s.sfx('thud');
    window.setTimeout(() => {
      this.skipTo(minutesUntilWake(store.minutes) + 60);
      store.incr('passOuts');
      store.addStat('health', -10);
      store.addStat('buzz', -100);
      const b = rng.pick(BLACKOUTS);
      this.s.teleportTo(b.spot);
      let text = b.outcome.text ?? '';
      if (store.money > 5 && rng.chance(0.35)) {
        const lost = Math.min(Math.round(store.money * 0.3), 40);
        store.spend(lost);
        text += `\n\nYour wallet is $${lost} lighter. Dale was seen buying scratch-offs at 6 AM.`;
      }
      window.setTimeout(() => {
        ui.hud.hideFade();
        this.sleeping = false;
        ui.modal.show({ title: 'Where... Am I?', art: ':dizzy:', text, choices: [], leave: 'Walk of shame' });
        store.apply({ ...b.outcome, text: undefined });
      }, 700);
    }, 1800);
  }

  emergencyRoom(): void {
    if (this.sleeping) return;
    const { store, ui } = this.s;
    this.interruptEverything();
    this.sleeping = true;
    ui.hud.showFade(':ambulance:', 'WEE-OOO WEE-OOO');
    this.s.sfx('siren');
    window.setTimeout(() => {
      this.skipTo(minutesUntilWake(store.minutes) + 60);
      store.state.stats.health = 55;
      store.state.stats.buzz = 0;
      store.addDebt(CONFIG.erBill);
      store.incr('erVisits');
      this.wakeAtDoor();
      window.setTimeout(() => {
        ui.hud.hideFade();
        this.sleeping = false;
        ui.modal.show({
          title: 'Mercy General Hospital',
          art: ':hospital:',
          text: `You woke up in the ER. The doctor says your blood is 40% Mtn Dewd. The bill is $${CONFIG.erBill}. Lucky's Payday "helpfully" covered it. You owe them now, garnished on rent day.`,
          choices: [],
          leave: 'Thanks, America',
        });
      }, 700);
    }, 2000);
  }

  private interruptEverything(): void {
    this.driving.stop(true);
    this.s.stunts.cancel();
  }

  /** Skips the clock ahead (as if asleep), handling new days. */
  skip(minutes: number): void {
    this.skipTo(minutes);
  }

  private skipTo(minutes: number): void {
    const days = this.s.store.advanceTime(minutes, { asleep: true });
    if (days > 0) this.s.onNewDay();
  }

  private wakeAtDoor(): void {
    const door = this.s.home().door;
    this.s.player.teleport(door.x, door.z + 0.6, 0);
    this.s.cameraRig.snap();
  }

  private finishFade(): void {
    this.s.ui.hud.hideFade();
    this.sleeping = false;
    this.s.onUiClosed();
  }
}
